import type { ReactElement } from "react"
import { useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useSelector } from "react-redux"

import CloseIcon from "@mui/icons-material/Close"
import { Dialog, DialogContent, DialogTitle, Grid, IconButton, useMediaQuery, useTheme } from "@mui/material"
import { Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError } from "axios"
import moment from "moment"

import { LogViewer } from "../../../components/data-display/LogViewer"
import { getTheme } from "../../../hooks/UseTheme"
import { KonanAPI } from "../../../services/KonanAPI"
import type { RootState } from "../../../store/ReduxStore"
import type { DeploymentLogs } from "../../../types/generated/api/DeploymentLogs"
import type { LogsDialogProps } from "../interfaces"

/**
 * Container component for handling model training logs functionality.
 * Manages log viewing dialog and data fetching.
 *
 * @param {boolean} isOpen - Controls the visibility state of the dialog
 * @param {Dispatch} setIsOpen - Function to handle dialog close state
 * @returns {ReactElement}
 */
export function LogsDialog(props: Readonly<LogsDialogProps>): ReactElement {
  const { isOpen, setIsOpen } = props

  const currentModel = useSelector((state: RootState) => state.models.currentModel)

  const theme = getTheme()
  const MuiTheme = useTheme()

  const isSmDown = useMediaQuery(MuiTheme.breakpoints.down("sm"))
  const { t } = useTranslation("models")

  // Start date for log filtering
  const [startDate, setStartDate] = useState<moment.Moment | null>(moment().subtract(6, "days"))
  // End date for log filtering
  const [endDate, setEndDate] = useState<moment.Moment | null>(moment())

  // Query for fetching model logs
  // Includes date range filtering and auto-refresh
  const { isLoading, data = { logs: [] } } = useQuery<DeploymentLogs, AxiosError>(
    ["project-logs", currentModel, startDate, endDate],
    () =>
      KonanAPI.fetchModelLogs({
        start_date: startDate?.toISOString() as string,
        end_date: endDate?.toISOString() as string,
        model_uuid: currentModel?.uuid,
      }),
    {
      // Only fetch when no date range is selected
      enabled: !!startDate && !!endDate && currentModel?.state !== "disabled" && isOpen,
      // Refresh logs every 3 seconds
      refetchInterval: 3000,
    },
  )

  const lastWeekOnly = (date: moment.Moment): boolean => {
    return moment().subtract(7, "days") < date && date < moment()
  }

  return (
    // Model Logs Dialog
    <Dialog open={isOpen} onClose={() => setIsOpen(false)} fullScreen={isSmDown} maxWidth="lg" fullWidth>
      <DialogTitle className="dialog-header-base">
        <Grid item container justifyContent="space-between" xs={12}>
          <Typography variant="h2-bold">{t("dialogs.logs.title")}</Typography>

          <IconButton id="close" onClick={() => setIsOpen(false)} size="small" className={"close-icon-button"}>
            <CloseIcon style={{ color: theme.palette.gray.background[1] }} />
          </IconButton>
        </Grid>
      </DialogTitle>

      <DialogContent className="dialog-base">
        <LogViewer
          data={data.logs}
          isLoading={isLoading}
          enableDatePicker={true}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          // TODO:: fix in SUI to enable both options at the same time
          // disableFuture
          isOutSideRange={lastWeekOnly}
          minHeight={isSmDown ? "calc(100% - 142px)" : "60vh"}
        />
      </DialogContent>
    </Dialog>
  )
}
